import { ChangeDetectorRef, Component, Inject, OnDestroy, OnInit } from '@angular/core';
import {
  AbstractControl,
  FormsModule,
  ReactiveFormsModule,
  UntypedFormArray,
  UntypedFormControl,
  UntypedFormGroup,
  ValidationErrors,
} from '@angular/forms';
import { MatDialog } from '@angular/material/dialog';
import {
  AppKey,
  AppPartnerService,
  AppPricingService as MarketplaceAppsPricingService,
  FieldMask,
  UnitBillingFrequency,
} from '@galaxy/marketplace-apps';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { RuleInterface } from '@vendasta/automata';
import { Frequency as GalaxyFrequency } from '@vendasta/galaxy/frequency';
import { RevenuePeriod } from '@vendasta/sales-orders';
import { AppWithRetailPricing, SalesOrderLineItemSelectorDialogComponent } from '@vendasta/sales-ui';
import { BehaviorSubject, combineLatest, Observable, of, Subject } from 'rxjs';
import { map, shareReplay, skipWhile, switchMap, take } from 'rxjs/operators';
import { AUTOMATION_MARKET_ID_INJECTION_TOKEN$ } from '@galaxy/automata/shared';
import { CommonActionPanelComponent } from '../../../common/common-action-panel-component.directive';
import { positiveIntegerWithMaxValidatorBuilder } from '../../../common/utils/number-validator';
import { periodDays, periodHours, rateLimitingTypeAnyEntity } from '../../rate-filter';
import { CommonModule } from '@angular/common';
import { GalaxyAlertModule } from '@vendasta/galaxy/alert';
import { MatButtonModule } from '@angular/material/button';
import { MatCardModule } from '@angular/material/card';
import { GalaxyFormFieldModule } from '@vendasta/galaxy/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatIconModule } from '@angular/material/icon';
import { VaIconModule } from '@vendasta/uikit';
import { MatListModule } from '@angular/material/list';
import { BillingUiModule } from '@vendasta/billing-ui';
import { MatSelectModule } from '@angular/material/select';
import { ItemSelectorModule } from '@galaxy/inventory-ui';

interface RecurringChargeTotal {
  total: number;
  isStartingPrice: boolean;
  frequency?: GalaxyFrequency;
}

interface PricingComponentItems {
  pricing: number;
  frequency: GalaxyFrequency;
  commitment: any;
  currency: string;
}

interface AppAndEditionKeys {
  appId: string;
  editionId: string;
}

const ADDON_TYPE = 2;

@Component({
  selector: 'automata-activate-product-panel',
  templateUrl: './activate-limited-products-panel.component.html',
  styleUrls: ['./activate-limited-products-panel.component.scss'],
  imports: [
    CommonModule,
    TranslateModule,
    GalaxyAlertModule,
    MatButtonModule,
    MatCardModule,
    FormsModule,
    GalaxyFormFieldModule,
    ReactiveFormsModule,
    MatInputModule,
    MatIconModule,
    MatSelectModule,
    VaIconModule,
    MatListModule,
    BillingUiModule,
    ItemSelectorModule,
  ],
})
export class ActivateLimitedProductsPanelComponent extends CommonActionPanelComponent implements OnInit, OnDestroy {
  selectedProducts$$: BehaviorSubject<AppWithRetailPricing[]> = new BehaviorSubject<AppWithRetailPricing[]>([]);
  selectedProducts$: Observable<AppWithRetailPricing[]> = this.selectedProducts$$.asObservable();
  appPriceDictionary: { [appId: string]: PricingComponentItems } = {};
  selectedAppsAndEditionsIds: { [appId: string]: AppAndEditionKeys } = {};
  total = 0;
  recurringTotals$: Observable<{ [frequency: string]: RecurringChargeTotal }> = of({});

  form: UntypedFormGroup;
  periodHours = periodHours;
  periodDays = periodDays;

  destroyed$$ = new Subject<void>();

  constructor(
    protected readonly translateService: TranslateService,
    protected readonly appPricingService: MarketplaceAppsPricingService,
    protected readonly appv2Service: AppPartnerService,
    private readonly ref: ChangeDetectorRef,
    @Inject(AUTOMATION_MARKET_ID_INJECTION_TOKEN$)
    private readonly marketId$: Observable<string>,
    public dialog: MatDialog,
  ) {
    super();
  }

  ngOnInit(): void {
    super.ngOnInit();

    const jsonData = this.step ? JSON.parse(this.step.data) : {};
    this.form = new UntypedFormGroup({
      frequency: new UntypedFormControl(jsonData.frequency || 10, positiveIntegerWithMaxValidatorBuilder(500)),
      length: new UntypedFormControl(jsonData.length || 5, positiveIntegerWithMaxValidatorBuilder(100)),
      period: new UntypedFormControl(jsonData.period || this.periodDays, periodValidator),
    });

    const lineItems = jsonData.line_items || [];
    const preselectedAppKeys = lineItems.map(
      (l) => <AppKey>{ appId: l.identifier?.app_key?.app_id, editionId: l.identifier?.app_key?.edition_id },
    );

    this.automation.marketId
      ? this.appv2Service
          .getMulti(preselectedAppKeys, this.automation.namespace, this.automation.marketId, null, true)
          .pipe(take(1))
          .subscribe((apps) => this.selectedProducts$$.next((apps || []) as AppWithRetailPricing[]))
      : this.marketId$
          .pipe(
            take(1),
            switchMap((marketId) =>
              this.appv2Service.getMulti(preselectedAppKeys, this.automation.namespace, marketId, null, true),
            ),
          )
          .subscribe((apps) => this.selectedProducts$$.next((apps || []) as AppWithRetailPricing[]));

    this.selectedProducts$
      .pipe(
        skipWhile((appKeys) => !!appKeys && appKeys.length < 1),
        switchMap((apps) => {
          const appAndEditionKey: { [id: string]: AppAndEditionKeys } = {};
          apps.forEach(
            (app) => (appAndEditionKey[app.key.appId] = { appId: app.key.appId, editionId: app.key.editionId || '' }),
          );
          this.selectedAppsAndEditionsIds = appAndEditionKey;
          if (apps.length > 0) {
            this.savingEnabled = true;
            return this.appPricingService.getMultiPricing(
              apps.map((app) => app.key.appId),
              new FieldMask({ paths: ['wholesale'] }),
              this.automation.namespace,
              this.automation.marketId,
            );
          }
          this.savingEnabled = false;
          return of([]);
        }),
        map((prices) => {
          const appPrices: { [id: string]: PricingComponentItems } = {};
          this.total = 0;
          prices.forEach((price) => {
            const currency = Object.keys(
              price.pricesForContexts.wholesale.pricesForEditions[
                this.selectedAppsAndEditionsIds[price.appId].editionId
              ].pricesForCurrencies,
            )[0];
            appPrices[price.appId] = {
              pricing:
                price.pricesForContexts.wholesale.pricesForEditions[
                  this.selectedAppsAndEditionsIds[price.appId].editionId
                ].pricesForCurrencies[currency].pricesForFrequencies[0].pricingRules[0].price,
              currency: currency,
              frequency: this.frequencyToGalaxyFrequency(
                price.pricesForContexts.wholesale.pricesForEditions[
                  this.selectedAppsAndEditionsIds[price.appId].editionId
                ].pricesForCurrencies[currency].pricesForFrequencies[0].frequency,
              ),
              commitment:
                price.pricesForContexts.wholesale.pricesForEditions[
                  this.selectedAppsAndEditionsIds[price.appId].editionId
                ].pricesForCurrencies[currency].pricesForFrequencies[0].commitment,
            };
            this.total += appPrices[price.appId].pricing || 0;
          });
          this.appPriceDictionary = appPrices;
          this.recurringTotals$ = this.calculatePriceFrequencies(this.appPriceDictionary);
          this.ref.detectChanges();
        }),
        shareReplay(1),
      )
      .subscribe();

    this.ref.detectChanges();
  }

  calculatePriceFrequencies(appPriceDictionary: any): any {
    const recurringTotals: { [frequency: string]: RecurringChargeTotal } = {};
    recurringTotals[GalaxyFrequency.MONTHLY] = { total: 0, isStartingPrice: false, frequency: GalaxyFrequency.MONTHLY };
    recurringTotals[GalaxyFrequency.YEARLY] = { total: 0, isStartingPrice: false, frequency: GalaxyFrequency.YEARLY };
    // tslint:disable-next-line:forin
    for (const x in appPriceDictionary) {
      switch (appPriceDictionary[x].frequency) {
        case GalaxyFrequency.MONTHLY:
          recurringTotals[GalaxyFrequency.MONTHLY].total += appPriceDictionary[x].pricing || 0;
          break;
        case GalaxyFrequency.YEARLY:
          recurringTotals[GalaxyFrequency.YEARLY].total += appPriceDictionary[x].pricing || 0;
          break;
      }
    }
    for (const key of Object.keys(recurringTotals)) {
      if (recurringTotals[key].total === 0) {
        delete recurringTotals[key];
      }
    }
    return of(recurringTotals);
  }

  ngOnDestroy(): void {
    this.destroyed$$.next();
  }

  private frequencyToGalaxyFrequency(frequency: UnitBillingFrequency): GalaxyFrequency {
    switch (frequency) {
      case UnitBillingFrequency.UNIT_BILLING_FREQUENCY_MONTHLY:
        return GalaxyFrequency.MONTHLY;
      case UnitBillingFrequency.UNIT_BILLING_FREQUENCY_YEARLY:
        return GalaxyFrequency.YEARLY;
      case UnitBillingFrequency.UNIT_BILLING_FREQUENCY_ONETIME:
        return GalaxyFrequency.ONE_TIME;
    }
  }

  private galaxyFrequencyToRevenuePeriod(period: GalaxyFrequency): RevenuePeriod {
    switch (period) {
      case GalaxyFrequency.MONTHLY:
        return RevenuePeriod.MONTHLY;
      case GalaxyFrequency.YEARLY:
        return RevenuePeriod.YEARLY;
      case GalaxyFrequency.ONE_TIME:
        return RevenuePeriod.ONETIME;
      default:
        return RevenuePeriod.ONETIME;
    }
  }

  getRules(): RuleInterface[] {
    return [];
  }

  getData(): any {
    const jsonData = this.step ? JSON.parse(this.step.data) : {};
    if (!this.form) {
      return jsonData;
    }

    if (this.selectedProducts$$.value?.length > 0) {
      jsonData.line_items = this.selectedProducts$$.value.map((app) => {
        const app_key = {
          app_id: app.key.appId,
          edition_id: app.key.editionId || '',
        };
        return {
          identifier: {
            app_key,
          },
          quantity: 1,
          is_trial: false,
          currency_code: this.appPriceDictionary[app.key.appId].currency,
          current_revenue: {
            revenue_components: [
              {
                value: this.appPriceDictionary[app.key.appId].pricing || 0,
                period: this.galaxyFrequencyToRevenuePeriod(this.appPriceDictionary[app.key.appId].frequency),
                is_starting_revenue: false,
              },
            ],
          },
        };
      });
    }

    jsonData.rate_limiting_type = rateLimitingTypeAnyEntity;
    jsonData.frequency = this.form.controls.frequency.value;
    jsonData.length = this.form.controls.length.value;
    jsonData.period = this.form.controls.period.value;
    return jsonData;
  }

  getControlsToValidateOnSave(): (UntypedFormControl | UntypedFormGroup | UntypedFormArray)[] {
    return [this.form];
  }

  isFormValid(): boolean {
    return this.form.valid;
  }

  openProductSelectorModal(): void {
    combineLatest([this.marketId$, this.selectedProducts$])
      .pipe(
        take(1),
        switchMap(([marketId, selectedProducts]) => {
          return this.dialog
            .open(SalesOrderLineItemSelectorDialogComponent, {
              data: {
                marketId: this.automation.marketId ?? marketId,
                preselectedApps: selectedProducts,
                preselectedPackages: [],
                hideOrderFormProducts: true,
                hidePackages: true,
                wholesalePricing: true,
              },
              minWidth: '750px',
              maxHeight: '95vh',
            })
            .afterClosed();
        }),
      )
      .subscribe((selection) => {
        if (selection?.apps) {
          this.selectedProducts$$.next([...selection.apps]);
        }
      });
  }

  toProductType(appType: number): string {
    if (appType === ADDON_TYPE) {
      return this.translateService.instant('COMMON.ADD_ON');
    }
    return this.translateService.instant('COMMON.PRODUCT');
  }

  removeProduct(app: AppWithRetailPricing): void {
    this.selectedProducts$$.next(this.selectedProducts$$.value.filter((p) => p !== app));
  }
}

function periodValidator(control: AbstractControl): ValidationErrors | null {
  if (control.value !== periodDays && control.value !== periodHours) {
    return { invalid: true };
  }
  return null;
}
